/**
 * La pantalla de UN programa: filtros → preview → resumen → confirmar. Una instancia por pestaña.
 *
 * Es el cuerpo que antes vivía entero en `App.tsx`, extraído cuando llegó el segundo programa
 * (soft killers, BB-2004). Lo que cambia entre programas viene en `programa` (`programas.ts`) y en
 * las dos funciones inyectadas: qué callable previsualiza y cuál confirma. Todo lo demás —el preview
 * como acción explícita, mandar solo `previewId`, pintar las cifras tal cual llegan, la semántica de
 * "sin filtros"— es idéntico y por eso está una sola vez.
 *
 * Decisiones que conviene no deshacer:
 *   - El preview es una acción explícita. No se relanza al cambiar un filtro: cada uno es un escaneo
 *     facturado de BigQuery, y un refetch automático acuñaría un previewId nuevo con las cifras
 *     viejas en pantalla.
 *   - Al confirmar se manda SOLO previewId.
 *   - Las cifras se pintan tal como llegan. El front no agrega.
 *   - Sin filtros NO es un caso degradado: es el modo de explorar la distribución de todo lo
 *     pendiente. Pero entonces confirmar significa pagarlo todo, y eso se dice explícitamente.
 *   - Los filtros de arriba son el único filtro (2026-09-10). Nada del resumen se puede clicar.
 */
import { Alert, Badge, Button, Card, Group, Modal, MultiSelect, Stack, Text } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import type {
  ConfirmRequest,
  ConfirmResponse,
  Criterios,
  PreviewResponse,
} from '@caes-payments/shared';
import { useCallback, useEffect, useState } from 'react';

import { describirAlcance, mismosCriterios, sinFiltros } from '../alcance';
import {
  aplicarBlacklist,
  blacklistNoAplicado,
  FILTRO_BLACKLIST_VACIO,
  type FiltroBlacklist as Blacklist,
} from '../blacklist';
import {
  aplicarCertificacion,
  FILTRO_CERTIFICACION_VACIO,
  opcionesDeCertificacion,
  type FiltroCertificacion as Certificacion,
} from '../certificacion';
import { mensajeDeConfirmacion, type AvisoConfirmacion } from '../confirmacion';
import type { FicherosLeidos as DatosFicheros } from '../ficheros';
import { aFechaLocal, formatearNumero } from '../formato';
import type { ProgramaConsola } from '../programas';
import { rangoCompleto, rangoIncompleto, type Rango } from '../rango';
import { acumularVocabulario, VOCABULARIO_VACIO, type Vocabulario } from '../vocabulario';
import { FicherosLeidos, type EstadoFicheros } from './FicherosLeidos';
import { FiltroBlacklist } from './FiltroBlacklist';
import { FiltroCertificacion } from './FiltroCertificacion';
import { ResumenPanel } from './ResumenPanel';

function mensajeDeError(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/**
 * Qué se dice bajo cada desplegable. Tres estados, y el del medio es el que faltaba: una lista que
 * tiene valores pero NO todos, porque salio de un preview filtrado. Callarse eso haria creer que no
 * existen mas comunidades que las que se ven.
 */
function descripcionFiltro(nOpciones: number, completo: boolean, textoCompleto: string): string {
  if (nOpciones === 0) return 'Se rellena al ver todo lo pendiente';
  if (!completo) return 'Lista parcial: pulsa "Ver todo lo pendiente" para todas';
  return textoCompleto;
}

const CARGAR_VALORES = 'Pulsa "Ver todo lo pendiente" para cargar los valores';

export function PanelPrograma({
  programa,
  previsualizar: llamarPreview,
  confirmar: llamarConfirm,
  onPreview,
  onConfirmado,
  refrescarToken,
  vocabularioInicial = VOCABULARIO_VACIO,
  cargarFicheros,
}: {
  programa: ProgramaConsola;
  /** La callable de preview de ESTE programa. Ya desenvuelta: devuelve la respuesta, no `{ data }`. */
  previsualizar: (criterios: Criterios) => Promise<PreviewResponse>;
  /** `/confirm`. Solo se llama si `programa.confirmacion.habilitada`. */
  confirmar: (req: ConfirmRequest) => Promise<ConfirmResponse>;
  /** Cada preview que llega: la app lee de ahí `mode` y `limiteRpsPago`, que son globales. */
  onPreview?: (preview: PreviewResponse) => void;
  /** Tras encolar con éxito. La app cambia a la pestaña del registro y enseña el aviso allí. */
  onConfirmado: (aviso: AvisoConfirmacion) => void;
  /** Para el caso "me acaban de dar de alta": fuerza un token nuevo antes de reintentar. */
  refrescarToken?: () => Promise<void>;
  /** Solo la vista de diseño lo usa, para que los desplegables no empiecen vacíos. */
  vocabularioInicial?: Vocabulario;
  /**
   * Los ficheros de certificación que la consola está viendo y cuándo entró el último desde Drive
   * (ver `ficheros.ts`). Consulta de SOLO LECTURA sobre el universo completo, independiente del
   * filtro: se carga al montar el panel y se puede recargar a mano. Solo tiene sentido en programas
   * con `filtroCertificacion`; si no se inyecta, el bloque no se pinta.
   */
  cargarFicheros?: () => Promise<DatosFicheros>;
}) {
  const [regiones, setRegiones] = useState<string[]>([]);
  const [rango, setRango] = useState<Rango>([null, null]);
  const [mitecoStatus, setMitecoStatus] = useState<string[]>([]);
  /**
   * Certificación MITECO (BB-2000): UN filtro con dos dimensiones, periodo o fichero, que van a
   * `certificationPeriod` o `driveFileName` del contrato. Un solo estado y no dos listas porque las
   * dos dimensiones no se combinan entre sí — ver `certificacion.ts`. Solo en programas cuya tabla
   * tiene esas columnas.
   */
  const [certificacion, setCertificacion] = useState<Certificacion>(FILTRO_CERTIFICACION_VACIO);
  /** Motivo de soft killer (BB-2004). Solo en el programa `softKiller`. */
  const [motivos, setMotivos] = useState<string[]>([]);
  /** Conductor en blacklist (BB-2608). "Todos" no viaja en `criterios`: ver `blacklist.ts`. */
  const [blacklist, setBlacklist] = useState<Blacklist>(FILTRO_BLACKLIST_VACIO);

  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  /**
   * Los criterios que el front MANDÓ en ese preview. Para avisar si los filtros de pantalla cambiaron
   * después: el confirm paga el preview, no la pantalla. Lo enviado y no el eco del backend, para que
   * la comparación no dependa de cómo el backend devuelva los criterios.
   */
  const [criteriosDelPreview, setCriteriosDelPreview] = useState<Criterios | null>(null);
  const [cargandoPreview, setCargandoPreview] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Valores seleccionables, acumulados de los previews de ESTE programa.
   *
   * El contrato no tiene endpoint de opciones, y el CLAUDE.md dice que el vocabulario lo sirve el
   * backend y no se hardcodea en la UI. Los desgloses de un preview son exactamente los valores que
   * existen en ese conjunto, así que sirven de vocabulario sin endpoint nuevo. Por programa, porque
   * los universos son distintos: los estados MITECO de soft killers son los RECHAZADO_*, que en
   * elegibles no existen.
   */
  const [vocabulario, setVocabulario] = useState<Vocabulario>(vocabularioInicial);

  /**
   * Ficheros leídos de Drive. Es una LECTURA, no una mutación como el preview: no acuña nada ni deja
   * rastro, así que cargarla sola al montar no rompe la regla de "el preview es una acción explícita".
   */
  const [ficheros, setFicheros] = useState<EstadoFicheros>({ estado: 'cargando' });
  const cargarListaDeFicheros = useCallback(async () => {
    if (!cargarFicheros) return;
    setFicheros({ estado: 'cargando' });
    try {
      setFicheros({ estado: 'ok', datos: await cargarFicheros() });
    } catch (err) {
      setFicheros({ estado: 'error', mensaje: mensajeDeError(err) });
    }
  }, [cargarFicheros]);
  useEffect(() => {
    if (programa.filtroCertificacion) void cargarListaDeFicheros();
  }, [programa.filtroCertificacion, cargarListaDeFicheros]);

  /** Construye los criterios. Vacío se OMITE: el backend lo interpreta como "todos". */
  const criteriosActuales = useCallback((): Criterios => {
    let criterios: Criterios = {};
    // El programa viaja explícito solo cuando no es el de siempre: así los previews de elegibles son
    // byte a byte los de antes, y los de soft killers quedan autodescritos en el audit log. El
    // backend lo fuerza igualmente en su callable; esto es para quien lea `criterios` después.
    if (programa.id !== 'elegibles') criterios.programa = programa.id;
    if (regiones.length > 0) criterios.regionName = regiones;
    if (mitecoStatus.length > 0) criterios.caesMitecoStatus = mitecoStatus;
    if (programa.filtroCertificacion) criterios = aplicarCertificacion(criterios, certificacion);
    if (programa.filtroMotivo && motivos.length > 0) criterios.caesSoftKillerReason = motivos;
    if (programa.filtroBlacklist) criterios = aplicarBlacklist(criterios, blacklist);
    // Solo con los DOS extremos. Un rango a medias no se manda —y por eso el boton esta
    // deshabilitado mientras lo este: si se enviara, la ausencia de rangoFechas significaria TODAS
    // las fechas, que es exactamente lo contrario de lo que alguien acaba de empezar a seleccionar.
    if (rangoCompleto(rango)) {
      criterios.rangoFechas = { desde: aFechaLocal(rango[0]!), hasta: aFechaLocal(rango[1]!) };
    }
    return criterios;
  }, [programa, regiones, mitecoStatus, certificacion, motivos, blacklist, rango]);

  const filtrosVacios = sinFiltros(criteriosActuales());

  async function previsualizar() {
    setError(null);
    setCargandoPreview(true);
    const criterios = criteriosActuales();
    try {
      const res = await llamarPreview(criterios);
      setPreview(res);
      setCriteriosDelPreview(criterios);
      onPreview?.(res);
      // De CUALQUIER preview, no solo del que va sin filtros. Antes solo se rellenaba sin filtros,
      // y quien empezaba filtrando por fecha se quedaba con los desplegables vacios sin salida.
      setVocabulario((previo) =>
        acumularVocabulario(
          previo,
          {
            regiones: res.resumen.porCa.map((f) => f.regionName),
            miteco: res.resumen.porMitecoStatus.map((f) => f.caesMitecoStatus),
            ficheros: res.resumen.porDriveFileName.map((f) => f.driveFileName),
            periodos: res.resumen.porCertificationPeriod.map((f) => f.certificationPeriod),
            motivos: (res.resumen.porSoftKillerReason ?? []).map((f) => f.caesSoftKillerReason),
          },
          sinFiltros(criterios),
        ),
      );
    } catch (err) {
      setError(mensajeDeError(err));
      // Se descarta el preview anterior: dejarlo tras un fallo invita a confirmar cifras que ya no
      // corresponden a los filtros visibles.
      setPreview(null);
    } finally {
      setCargandoPreview(false);
    }
  }

  async function confirmar() {
    // El bloqueo de `filtroBlacklistIgnorado` se repite aquí y en el botón del modal, no solo en el de
    // fuera: así no depende de que el modal se abra únicamente desde ese botón.
    if (!preview || filtroBlacklistIgnorado || !programa.confirmacion.habilitada) return;
    setConfirmando(true);
    setError(null);
    try {
      const res = await llamarConfirm({ previewId: preview.previewId });
      // BB-1617: /confirm devuelve el acuse de ENCOLADO, no el resultado del pago. El resultado real
      // vive en el audit log, así que el mensaje dice qué se encoló y dónde mirar — nunca "pagado".
      const { estado, totalViajes, totalChunks } = res;

      if (estado !== 'encolado' && estado !== 'completado') {
        // Falla cerrado: un valor que no se reconoce no se pinta como éxito.
        setError(`El backend devolvió un estado que no reconozco: ${String(estado)}`);
        return;
      }

      // `mode` viene del preview, no del confirm: es el modo con el que se calculó lo aprobado.
      onConfirmado(mensajeDeConfirmacion({ estado, totalViajes, totalChunks, mode: preview.mode }));
      // El preview se invalida: ese conjunto ya está encolado.
      setPreview(null);
      setModalAbierto(false);
    } catch (err) {
      setError(mensajeDeError(err));
    } finally {
      setConfirmando(false);
    }
  }

  /** Fuerza un token nuevo y reintenta el preview. Para el caso "me acaban de dar de alta". */
  async function reintentarConTokenNuevo() {
    await refrescarToken?.();
    await previsualizar();
  }

  const previewSinFiltros = preview ? sinFiltros(preview.criterios) : false;
  const confirmacion = programa.confirmacion;
  /**
   * Los filtros de pantalla ya no son los del preview. Confirmar sigue pagando el PREVIEW —solo viaja
   * `previewId`, invariante 3—, así que esto no cambia qué se paga: cambia lo que Ops cree que paga.
   * Por eso se avisa en el modal, con lo que de verdad se encolaría.
   */
  const filtrosCambiados =
    preview !== null &&
    criteriosDelPreview !== null &&
    !mismosCriterios(criteriosActuales(), criteriosDelPreview);
  /**
   * El backend ignoró el filtro de blacklist que se le pidió (ver `blacklistNoAplicado`). Entonces el
   * conjunto NO es el que dice el alcance, y confirmar se bloquea: es el único caso en que la pantalla
   * describiría mal lo que se paga.
   */
  // Lo pedido se mira en los DOS sitios —el eco del backend y lo que se mandó—, para que esta
  // comprobación no se apague en silencio si el backend dejara de devolver la clave.
  const blacklistPedido =
    preview?.criterios.isDriverBlacklisted ?? criteriosDelPreview?.isDriverBlacklisted;
  const filtroBlacklistIgnorado = preview
    ? blacklistNoAplicado({ isDriverBlacklisted: blacklistPedido }, preview.resumen)
    : false;

  return (
    <>
      <Stack gap="lg">
        {/* Una sola fila de filtros, arriba, que gobierna todo lo de abajo. */}
        <Card withBorder padding="md">
          <Stack gap="sm">
            <Text size="sm" c="dimmed">
              {programa.explicacion}
            </Text>
            <Group grow align="flex-start">
              {/* Todos llevan SIEMPRE una `description` de una linea. No es decoracion: es lo que
                  los mantiene alineados. Y el texto dice algo que Ops necesita saber: la semantica
                  del filtro vacio esta en el contrato y no es adivinable. */}
              <MultiSelect
                label="Comunidad autonoma"
                placeholder="Todas"
                description={descripcionFiltro(
                  vocabulario.regiones.length,
                  vocabulario.completo,
                  'Vacio = todas, incluidas las sin CA',
                )}
                /* Enabled a proposito aunque no haya valores: un control `disabled` sale del orden
                   de tabulacion y su texto no se anuncia. Pero entonces hay que explicar el vacio
                   AL ABRIRLO, o parece interactivo y no hace nada. */
                nothingFoundMessage={CARGAR_VALORES}
                data={vocabulario.regiones}
                value={regiones}
                onChange={setRegiones}
                searchable
                clearable
              />
              <DatePickerInput
                type="range"
                label="Fechas del viaje"
                placeholder="Todas"
                /* Sin esto Mantine NO deja elegir un solo dia. */
                allowSingleDateInRange
                /* Del VIAJE, no de la certificacion. Es la distincion que mas facil se asume mal. */
                description="Fecha del viaje, no de certificacion"
                value={rango}
                onChange={setRango}
                clearable
              />
              <MultiSelect
                label="Estado MITECO"
                placeholder="Todos"
                description={descripcionFiltro(
                  vocabulario.miteco.length,
                  vocabulario.completo,
                  'Vacio = todos los estados',
                )}
                nothingFoundMessage={CARGAR_VALORES}
                data={vocabulario.miteco}
                value={mitecoStatus}
                onChange={setMitecoStatus}
                searchable
                clearable
              />
            </Group>

            {(programa.filtroCertificacion || programa.filtroMotivo || programa.filtroBlacklist) && (
              <Group grow align="flex-start">
                {programa.filtroCertificacion && (
                  <Stack gap="xs">
                    {/* Certificacion MITECO, por periodo o por fichero (BB-2000). El interruptor
                        decide que lista se ve; cambiarlo vacia lo elegido, para que lo que se ve sea
                        siempre lo que se filtra. */}
                    <FiltroCertificacion
                      filtro={certificacion}
                      onCambiar={setCertificacion}
                      vocabulario={vocabulario}
                      descripcion={descripcionFiltro(
                        opcionesDeCertificacion(vocabulario, certificacion.dimension).length,
                        vocabulario.completo,
                        'Vacio = todos, incluidos los aun no certificados',
                      )}
                      nothingFoundMessage={CARGAR_VALORES}
                    />
                    {/* Qué ficheros está viendo la consola y cuándo entró el último desde Drive. SOLO
                        se mira: es para comprobar que lo que Ops subió ayer ya está, y nada de aquí
                        filtra. Debajo del filtro de certificación porque responde a la pregunta que
                        ese desplegable provoca ("¿por qué no me sale mi fichero?"). */}
                    {cargarFicheros && (
                      <FicherosLeidos
                        estado={ficheros}
                        onRecargar={() => void cargarListaDeFicheros()}
                      />
                    )}
                  </Stack>
                )}
                {programa.filtroMotivo && (
                  <MultiSelect
                    label="Motivo del soft killer"
                    placeholder="Todos"
                    description={descripcionFiltro(
                      vocabulario.motivos.length,
                      vocabulario.completo,
                      'Vacio = todos los motivos',
                    )}
                    nothingFoundMessage={CARGAR_VALORES}
                    data={vocabulario.motivos}
                    value={motivos}
                    onChange={setMotivos}
                    searchable
                    clearable
                  />
                )}
                {programa.filtroBlacklist && (
                  <FiltroBlacklist filtro={blacklist} onCambiar={setBlacklist} />
                )}
              </Group>
            )}

            <Group gap="sm">
              <Button
                onClick={() => void previsualizar()}
                loading={cargandoPreview}
                disabled={rangoIncompleto(rango)}
              >
                {filtrosVacios ? 'Ver todo lo pendiente' : 'Ver lo que contiene el filtro'}
              </Button>
              <Text size="xs" c={rangoIncompleto(rango) ? 'orange' : 'dimmed'}>
                {rangoIncompleto(rango)
                  ? 'Falta el otro extremo del rango. Para un solo dia, pulsa la misma fecha dos veces.'
                  : 'Sin filtros se ve la distribucion de todo lo pendiente, y de ahi salen los valores de los desplegables. Lo de abajo solo se mira: para acotar, cambia los filtros y vuelve a pulsar.'}
              </Text>
            </Group>
          </Stack>
        </Card>

        {error && (
          <Alert color="red" title="Error">
            {error}
            {/* Caso concreto y frecuente: te acaban de dar de alta y tu token es anterior. */}
            {error.includes('lista nombrada') && refrescarToken && (
              <Group mt="sm" gap="sm" align="center">
                <Button size="sm" variant="light" onClick={() => void reintentarConTokenNuevo()}>
                  Ya me han dado acceso, reintentar
                </Button>
                <Text size="xs" c="dimmed">
                  Si sigue fallando, sal y vuelve a entrar.
                </Text>
              </Group>
            )}
          </Alert>
        )}

        {preview && preview.resumen.nViajes === 0 && (
          <Alert color="gray" title="Ningun viaje con estos filtros">
            La combinacion de filtros no deja nada. Recuerda que al seleccionar CAs concretas los
            viajes sin CA quedan fuera, y que al elegir un periodo o un fichero quedan fuera los viajes
            aun no certificados.
          </Alert>
        )}

        {preview && preview.resumen.nViajes > 0 && (
          <div className={cargandoPreview ? 'recargando' : undefined}>
            <Stack gap="lg">
              <ResumenPanel
                resumen={preview.resumen}
                etiquetaAlcance={describirAlcance(preview.criterios)}
                datosActualizadosEn={preview.datosActualizadosEn}
                limiteRpsPago={preview.limiteRpsPago}
                distribuciones={programa.distribuciones}
                verboCifra={confirmacion.habilitada ? 'Se pagaría' : 'Retenido'}
              />

              {filtroBlacklistIgnorado && (
                <Alert color="red" title="El filtro de conductor en blacklist no se ha aplicado">
                  {`Has pedido "${describirAlcance({ isDriverBlacklisted: blacklistPedido })}", `}
                  pero el desglose del backend dice que el conjunto los incluye o no los distingue. Las
                  cifras de arriba no son las de ese filtro, asi que confirmar esta bloqueado. Avisa a
                  Data.
                </Alert>
              )}

              {confirmacion.habilitada ? (
                <div className="zona-confirmar">
                  <Group justify="space-between" align="center" wrap="wrap">
                    <div>
                      <Group gap="xs" mb={4}>
                        <Text size="sm" fw={700}>
                          Confirmar el pago
                        </Text>
                        {previewSinFiltros && <Badge color="orange">sin filtros</Badge>}
                      </Group>
                      <Text size="xs" c="dimmed">
                        {previewSinFiltros
                          ? 'Se encolaria TODO lo pendiente, no una seleccion. Acota por fecha, CA, estado, certificacion o blacklist si no es lo que quieres.'
                          : 'Se encola exactamente el conjunto que estas viendo.'}
                      </Text>
                    </div>
                    <Button
                      color="red"
                      onClick={() => setModalAbierto(true)}
                      disabled={cargandoPreview || filtroBlacklistIgnorado}
                    >
                      Confirmar {formatearNumero(preview.resumen.nViajes)} viajes
                    </Button>
                  </Group>
                </div>
              ) : (
                /* La zona existe igual, con el boton apagado: asi se ve DONDE estara y QUE falta para
                   activarlo, en vez de una pantalla a la que le falta algo sin decir que. */
                <div className="zona-confirmar">
                  <Group justify="space-between" align="center" wrap="wrap">
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text size="sm" fw={700} mb={4}>
                        El pago no se lanza desde aqui
                      </Text>
                      <Text size="xs" c="dimmed">
                        {confirmacion.motivo}
                      </Text>
                    </div>
                    <Button color="red" disabled>
                      Confirmar {formatearNumero(preview.resumen.nViajes)} viajes
                    </Button>
                  </Group>
                </div>
              )}
            </Stack>
          </div>
        )}
      </Stack>

      <Modal opened={modalAbierto} onClose={() => setModalAbierto(false)} title="Confirmar el pago">
        {preview && (
          <Stack>
            {filtrosCambiados && (
              <Alert color="orange" title="Los filtros han cambiado desde la ultima consulta">
                Se va a pagar lo que viste en el resumen ({describirAlcance(preview.criterios)}), no
                lo que marcan ahora los filtros. Para pagar lo de los filtros actuales, cancela y pulsa
                &quot;Ver lo que contiene el filtro&quot;.
              </Alert>
            )}
            {previewSinFiltros && (
              <Alert color="orange" title="No hay ningun filtro aplicado">
                Esto encola todo lo pendiente.
              </Alert>
            )}
            <Text>
              Se van a encolar <strong>{formatearNumero(preview.resumen.nViajes)} viajes</strong> por{' '}
              <strong>{preview.resumen.importeTotalEur} EUR</strong>.
            </Text>
            <Text size="sm" c="dimmed">
              Se paga exactamente el conjunto que acabas de ver: se manda la referencia de esa
              consulta, no los filtros. Backend paga al dia siguiente, asi que hay margen para
              pararlo.
            </Text>
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setModalAbierto(false)}>
                Cancelar
              </Button>
              <Button
                color="red"
                loading={confirmando}
                disabled={filtroBlacklistIgnorado}
                onClick={() => void confirmar()}
              >
                Confirmar
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  );
}
